import { el } from '../utils/dom.js';
import { TUTORIALS, GETTING_STARTED_STEPS } from './TutorialSteps.js';

const STORAGE_KEY = 'tileforge_tutorial_completed';

/**
 * Interactive step-by-step tutorial walkthrough.
 * Highlights UI elements with a spotlight overlay and positioned tooltips.
 */
export class Tutorial {
  constructor() {
    this._currentStep = 0;
    this._steps = GETTING_STARTED_STEPS;
    this._storageKey = STORAGE_KEY;
    this._overlay = null;
    this._tooltip = null;
    this._spotlight = null;
    this._savedStyles = null;
    this._resizeHandler = null;
  }

  /** Check if the default tutorial has been completed before. */
  static isCompleted() {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  }

  /** Mark default tutorial as completed. */
  static markCompleted() {
    localStorage.setItem(STORAGE_KEY, 'true');
  }

  /** Get all available tutorials. */
  static getAvailable() {
    return Object.entries(TUTORIALS).map(([id, t]) => ({
      id,
      name: t.name,
      description: t.description,
      completed: localStorage.getItem(t.storageKey) === 'true',
    }));
  }

  /**
   * Start a tutorial.
   * @param {string} [tutorialId] — ID from TUTORIALS registry. Default = 'getting-started'.
   */
  start(tutorialId = 'getting-started') {
    const tutorial = TUTORIALS[tutorialId];
    if (tutorial) {
      this._steps = tutorial.steps;
      this._storageKey = tutorial.storageKey;
    } else {
      this._steps = GETTING_STARTED_STEPS;
      this._storageKey = STORAGE_KEY;
    }

    this._currentStep = 0;
    this._createOverlay();
    this._showStep();

    this._resizeHandler = () => this._positionTooltip();
    window.addEventListener('resize', this._resizeHandler);

    this._keyHandler = (e) => {
      if (e.key === 'Escape') this._end(false);
      if (e.key === 'ArrowRight' || e.key === 'Enter') this._next();
      if (e.key === 'ArrowLeft') this._prev();
    };
    document.addEventListener('keydown', this._keyHandler);
  }

  _createOverlay() {
    // Remove any existing tutorial
    this._cleanup();

    this._overlay = el('div', { className: 'tutorial-overlay' });
    this._overlay.addEventListener('click', (e) => {
      if (e.target === this._overlay) e.stopPropagation();
    });

    this._tooltip = el('div', { className: 'tutorial-tooltip' });
    document.body.appendChild(this._overlay);
    document.body.appendChild(this._tooltip);
  }

  _showStep() {
    const step = this._steps[this._currentStep];
    if (!step) { this._end(true); return; }

    // Restore previous element
    this._clearHighlight();

    // Build tooltip content
    const total = this._steps.length;
    const num = this._currentStep + 1;

    this._tooltip.innerHTML = '';

    const counter = el('div', { className: 'tutorial-step-counter' }, `Step ${num} of ${total}`);
    const title = el('div', { className: 'tutorial-title' }, step.title);
    const desc = el('div', { className: 'tutorial-desc' }, step.text);
    const nav = el('div', { className: 'tutorial-nav' });

    const backBtn = el('button', {
      className: 'btn-outline tutorial-btn-back',
      onClick: () => this._prev(),
    }, 'Back');
    if (this._currentStep === 0) {
      backBtn.disabled = true;
      backBtn.style.opacity = '0.4';
      backBtn.style.cursor = 'default';
    }

    const isLast = this._currentStep === this._steps.length - 1;
    const nextBtn = el('button', {
      className: 'btn-gold tutorial-btn-next',
      onClick: () => this._next(),
    }, isLast ? 'Finish' : 'Next');

    const skipBtn = el('button', {
      className: 'btn-text tutorial-btn-skip',
      onClick: () => this._end(true),
    }, 'Skip Tutorial');

    nav.append(backBtn, nextBtn);
    this._tooltip.append(counter, title, desc, nav, skipBtn);

    // Arrow element
    this._arrow = el('div', { className: 'tutorial-arrow' });
    this._tooltip.appendChild(this._arrow);

    // Highlight target
    if (step.target) {
      const target = document.querySelector(step.target);
      if (target) {
        this._highlightElement(target);
      }
    }

    this._positionTooltip();

    // Fade in
    requestAnimationFrame(() => {
      this._tooltip.classList.add('visible');
    });
  }

  _highlightElement(target) {
    this._savedStyles = {
      el: target,
      position: target.style.position,
      zIndex: target.style.zIndex,
      boxShadow: target.style.boxShadow,
      outline: target.style.outline,
    };

    const computed = window.getComputedStyle(target);
    if (computed.position === 'static') {
      target.style.position = 'relative';
    }
    target.style.zIndex = '9001';
    target.style.boxShadow = '0 0 0 4px rgba(212, 160, 74, 0.6), 0 0 24px rgba(212, 160, 74, 0.3)';
    target.style.outline = 'none';
  }

  _clearHighlight() {
    if (this._savedStyles) {
      const { el: target, position, zIndex, boxShadow, outline } = this._savedStyles;
      target.style.position = position;
      target.style.zIndex = zIndex;
      target.style.boxShadow = boxShadow;
      target.style.outline = outline;
      this._savedStyles = null;
    }
  }

  _positionTooltip() {
    if (!this._tooltip) return;
    const step = this._steps[this._currentStep];
    if (!step) return;

    const tooltip = this._tooltip;
    const arrow = tooltip.querySelector('.tutorial-arrow');

    // Reset positioning
    tooltip.style.top = '';
    tooltip.style.left = '';
    tooltip.style.transform = '';
    if (arrow) {
      arrow.className = 'tutorial-arrow';
      arrow.style.left = '';
      arrow.style.top = '';
    }

    if (!step.target) {
      // Center on screen
      tooltip.style.top = '50%';
      tooltip.style.left = '50%';
      tooltip.style.transform = 'translate(-50%, -50%)';
      if (arrow) arrow.style.display = 'none';
      return;
    }

    const target = document.querySelector(step.target);
    if (!target) {
      tooltip.style.top = '50%';
      tooltip.style.left = '50%';
      tooltip.style.transform = 'translate(-50%, -50%)';
      if (arrow) arrow.style.display = 'none';
      return;
    }

    if (arrow) arrow.style.display = '';

    const rect = target.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 16;

    // Try to place below the element
    let top = rect.bottom + gap;
    let left = rect.left + rect.width / 2 - tooltipRect.width / 2;
    let arrowDir = 'top'; // arrow points upward

    // If tooltip goes below viewport, place above
    if (top + tooltipRect.height > vh - 10) {
      top = rect.top - tooltipRect.height - gap;
      arrowDir = 'bottom';
    }

    // If tooltip goes above viewport, place to the right
    if (top < 10) {
      top = rect.top + rect.height / 2 - tooltipRect.height / 2;
      left = rect.right + gap;
      arrowDir = 'left';

      if (left + tooltipRect.width > vw - 10) {
        left = rect.left - tooltipRect.width - gap;
        arrowDir = 'right';
      }
    }

    // Clamp horizontal
    left = Math.max(10, Math.min(left, vw - tooltipRect.width - 10));
    // Clamp vertical
    top = Math.max(10, Math.min(top, vh - tooltipRect.height - 10));

    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;

    // Position arrow
    if (arrow) {
      arrow.className = `tutorial-arrow tutorial-arrow-${arrowDir}`;

      if (arrowDir === 'top' || arrowDir === 'bottom') {
        const arrowLeft = Math.max(20, Math.min(
          rect.left + rect.width / 2 - left,
          tooltipRect.width - 20,
        ));
        arrow.style.left = `${arrowLeft}px`;
      } else {
        const arrowTop = Math.max(20, Math.min(
          rect.top + rect.height / 2 - top,
          tooltipRect.height - 20,
        ));
        arrow.style.top = `${arrowTop}px`;
      }
    }
  }

  _next() {
    this._tooltip.classList.remove('visible');
    this._currentStep++;
    if (this._currentStep >= this._steps.length) {
      this._end(true);
    } else {
      this._showStep();
    }
  }

  _prev() {
    if (this._currentStep <= 0) return;
    this._tooltip.classList.remove('visible');
    this._currentStep--;
    this._showStep();
  }

  _end(completed) {
    this._clearHighlight();
    if (completed) localStorage.setItem(this._storageKey, 'true');
    this._cleanup();
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }
    if (this._keyHandler) {
      document.removeEventListener('keydown', this._keyHandler);
      this._keyHandler = null;
    }
  }

  _cleanup() {
    if (this._overlay) { this._overlay.remove(); this._overlay = null; }
    if (this._tooltip) { this._tooltip.remove(); this._tooltip = null; }
  }
}
